import { Breadcrumbs, Page, PageHeader, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { FC, useContext, useState } from "react";

import ExceptionsFiltersContextProvider, { ExceptionsFiltersContext } from "@app/exceptions/contexts/ExceptionsFiltersContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { QESButton } from "@app/shared/qes-standards";
import ExceptionsDownload from "@app/exceptions/ExceptionsDownload";
import ExceptionsDownloadAllModal from "@app/exceptions/ExceptionsDownloadAllModal";
import GenerateDocumentFlyout from "@app/exceptions/GenerateDocumentFlyout";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { Link } from "react-router-dom";
import AddAttachmentModal from "@app/exceptions/AddAttachmentModal";
import ArchiveExceptionsModal from "@app/exceptions/ArchiveExceptionsModal";
import ExpireModal from "@app/exceptions/ExpireModal";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ExceptionsOverview } from "@app/exceptions/ExceptionsOverview";
import UnprocessedExceptionsBanner from "@app/exceptions/UnprocessedExceptionsBanner";
import EditExceptionFlyout from "@app/exceptions/EditExceptionFlyout";
import { clone } from "lodash";
import { ExceptionsDataModel, ExceptionsPaginationResult } from "@app/exceptions/interfaces";

type TabViews = "Overview" | "Downloads" | "Archived";

const WrappedExceptionsHome: FC = () => {

  const title = "Exceptions";
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const [activeTab, setActiveTab] = useState<TabViews>("Overview");
  const [isDownloadAllModalOpen, setIsDownloadAllModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const exceptionFilterContext = useContext(ExceptionsFiltersContext);
  const [isAddAttachmentModalOpen, setIsAddAttachmentModalOpen] = useState(false);
  const [isExpireModalOpen, setIsExpireModalOpen] = useState(false);
  const [enableMoreActionsButton, setEnableMoreActionsButton] = useState(true);
  const [exceptionsData, setExceptionsData] = useState<ExceptionsPaginationResult<ExceptionsDataModel> | null>(null);
  const defaultExceptionStatusesForBulkEdit = clone(exceptionFilterContext.exceptionStatusOptions);
  //Add the "None" or blank option for the bulk edit at the start of the array
  defaultExceptionStatusesForBulkEdit.unshift({ key: "none", name: "None" });

  const handleOpenDownloadAllModal = () => {
    setIsDownloadAllModalOpen(true);
  };

  const handleAddAttachmentsModal = () => {
    setIsAddAttachmentModalOpen(true);
  };

  const clearSelectedCheckboxes = () => {
    exceptionFilterContext.setSelectedExceptionIds([]);
  };

  const handleOpenArchiveModal = () => {
    setIsArchiveModalOpen(true);
  }

  const handleOpenExpireModal = () => {
    setIsExpireModalOpen(true);
  };

  const setTab = (tabName: string) => {
    if (tabName == "Downloads" || tabName == "Archived") {
      setEnableMoreActionsButton(false);
    } else {
      setEnableMoreActionsButton(true);
    }
    setActiveTab(tabName as TabViews);

    if (tabName == "Overview" && exceptionFilterContext.isForArchived
      || tabName == "Archived" && !exceptionFilterContext.isForArchived) {
      exceptionFilterContext.setTriggerRefreshData(true);
      exceptionFilterContext.setRequestModel(null);
      exceptionFilterContext.setHasLoadedFilterOptions(false);
      exceptionFilterContext.setHasSetInitialExceptionStatuses(false);
      setExceptionsData(null);
    }

    exceptionFilterContext.setPageIndex(1);
    exceptionFilterContext.setPageSize(100);
    exceptionFilterContext.setSelectedExceptionIds([]);
  }

  return (
    <>
      {featureFlags.has(Features.VOLUME_EXCEPTIONS) &&
        <Page documentTitle={title}>
          <Breadcrumbs items={[title]} />
          <PageInner>
            <PageHeader title={title} disableHR={true}>
              <PageHeaderRight>
                <div style={{ alignItems: 'center', display: 'flex', gap: '10px' }}>
                  <NewActionMenu
                    alignRight={true}
                    buttonText="More Actions"
                    containerClassnames="exceptions__more-actions-container"
                    testId="more-actions-select"
                    buttonClassNames="qes-button-primary"
                    disabled={!enableMoreActionsButton}
                  >
                    <FlyoutLink
                      classNames="action-menu-item col-12 font-weight-normal"
                      content={{
                        body: <GenerateDocumentFlyout
                          selectedExceptionIds={exceptionFilterContext.selectedExceptionIds}
                          resetSelectedCheckboxes={clearSelectedCheckboxes}
                          customProjectName={exceptionFilterContext.customProjectName}
                          enableRegulatoryExceptionReports={exceptionFilterContext.enableRegulatoryExceptionReports}
                          haveRemovedServiceArea={exceptionFilterContext.haveRemovedServiceArea}
                          haveRemovedSpecialty={exceptionFilterContext.haveRemovedSpecialty}
                          refreshData={() => exceptionFilterContext.setTriggerRefreshData(true)}
                        />,
                        title: "Generate Document",
                      }}
                      size="40"
                      testId="more-actions-select__generate-document"
                      disabled={exceptionFilterContext.selectedExceptionIds.length === 0}
                    >
                      Generate Document
                    </FlyoutLink>
                    <FlyoutLink
                      classNames="action-menu-item col-12 font-weight-normal"
                      content={{
                        body: <EditExceptionFlyout
                          exceptionStatusOptions={defaultExceptionStatusesForBulkEdit}
                          isForBulkEdit={true}
                        />,
                        title: `${exceptionFilterContext.selectedExceptionIds.length} exception${exceptionFilterContext.selectedExceptionIds.length == 1 ? "" : "s"} selected`
                      }}
                      size="40"
                      testId="more-actions-select__bulk-edit"
                      disabled={exceptionFilterContext.selectedExceptionIds.length === 0}
                    >
                      Edit
                    </FlyoutLink>
                    {featureFlags.has(Features.VOLUME_EXCEPTIONS_DOWNLOAD_ALL) &&
                      <NewActionMenuItem
                        display="Download All"
                        isSelected={false}
                        key="download-all"
                        onClick={handleOpenDownloadAllModal}
                        testId="more-actions-select__download-all"
                      />
                    }
                    {featureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) &&
                      <NewActionMenuItem
                        display="Add Attachments"
                        isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                        key="add-attachments"
                        onClick={handleAddAttachmentsModal}
                        testId="more-actions-select__add-attachments"
                      />
                    }
                    {featureFlags.has(Features.VOLUME_EXCEPTIONS_EXPIRE) &&
                      <NewActionMenuItem
                        display="Expire"
                        isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                        key="expire"
                        onClick={handleOpenExpireModal}
                        testId="more-actions-select__expire"
                      />
                    }
                    {featureFlags.has(Features.VOLUME_EXCEPTIONS_ARCHIVE) &&
                      <NewActionMenuItem
                        display="Archive"
                        isSelected={exceptionFilterContext.selectedExceptionIds.length === 0}
                        key="archive"
                        onClick={handleOpenArchiveModal}
                        testId="more-actions-select__archive"
                        aria-disabled={true}
                      />
                    }
                  </NewActionMenu>
                  {(featureFlags.has(Features.VOLUME_EXCEPTIONS_ADD_WIZARD) || featureFlags.has(Features.VOLUME_EXCEPTIONS_BULK_UPLOAD)) &&
                    <NewActionMenu
                      alignRight={true}
                      buttonText="Add Exception"
                      testId="add-exception"
                      buttonClassNames="qes-button-primary"
                    >
                      {featureFlags.has(Features.VOLUME_EXCEPTIONS_ADD_WIZARD) &&
                        <Link to={"./create"}>
                          <NewActionMenuItem
                            display="Create New"
                            isSelected={false}
                            key="create-new"
                            onClick={() => { }}
                            testId="add-exception__create-new"
                          />
                        </Link>
                      }

                      {featureFlags.has(Features.VOLUME_EXCEPTIONS_BULK_UPLOAD) &&
                        <Link to={"./bulk-upload"}>
                          <NewActionMenuItem
                            display="Upload File"
                            isSelected={false}
                            key="bulk-upload"
                            onClick={() => { }}
                            testId="add-exception__bulk-upload"
                          />
                        </Link>
                      }
                    </NewActionMenu>
                  }
                </div>
                <ExceptionsDownloadAllModal
                  isModalOpen={isDownloadAllModalOpen}
                  setIsModalOpen={setIsDownloadAllModalOpen}
                />
                <AddAttachmentModal
                  isModalOpen={isAddAttachmentModalOpen}
                  setIsModalOpen={setIsAddAttachmentModalOpen}
                  selectedExceptionIds={exceptionFilterContext.selectedExceptionIds}
                  resetSelectedCheckboxes={clearSelectedCheckboxes}
                />
                <ExpireModal
                  isModalOpen={isExpireModalOpen}
                  setIsModalOpen={setIsExpireModalOpen}
                  resetSelectedCheckboxes={clearSelectedCheckboxes}
                />
                <ArchiveExceptionsModal
                  isModalOpen={isArchiveModalOpen}
                  setIsModalOpen={setIsArchiveModalOpen}
                  resetSelectedCheckboxes={clearSelectedCheckboxes}
                />
              </PageHeaderRight>

            </PageHeader>
            <PageSection>
              <div className="tabs-style-2 mb-3">
                <ul className="nav nav-tabs" role="tablist">
                  <li>
                    <QESButton
                      qesType="none"
                      className={`nav-link text-dark font-weight-normal ${activeTab === "Overview" ? "active disabled" : ""}`}
                      qesTestId="exception-nav__tab--overview"
                      onClick={() => setTab("Overview" as TabViews)}
                      aria-disabled={activeTab === "Overview"}
                      role="tab"
                    >
                      Overview
                    </QESButton>
                  </li>
                  <li>
                    <QESButton
                      qesType="none"
                      className={`nav-link text-dark font-weight-normal ${activeTab === "Downloads" ? "active disabled" : ""}`}
                      qesTestId="exception-nav__tab--downloads"
                      onClick={() => setTab("Downloads" as TabViews)}
                      aria-disabled={activeTab === "Downloads"}
                      role="tab"
                    >
                      Downloads
                    </QESButton>
                  </li>
                  <li>
                    {featureFlags.has(Features.VOLUME_EXCEPTIONS_ARCHIVE) &&
                      <QESButton
                        qesType="none"
                        className={`nav-link text-dark font-weight-normal ${activeTab === "Archived" ? "active disabled" : ""}`}
                        qesTestId="exception-nav__tab--archived"
                        onClick={() => setTab("Archived" as TabViews)}
                        aria-disabled={activeTab === "Archived"}
                        role="tab"
                      >
                        Archived
                      </QESButton>
                    }
                  </li>
                </ul>
              </div>
            </PageSection>

            {activeTab === "Overview" &&
              <>
                <UnprocessedExceptionsBanner />
                <ExceptionsOverview isArchivedTable={false} exceptionsData={exceptionsData} setExceptionsData={setExceptionsData} />
              </>
            }
            {activeTab === "Downloads" &&
              <ExceptionsDownload />
            }
            {activeTab === "Archived" &&
              <ExceptionsOverview isArchivedTable={true} exceptionsData={exceptionsData} setExceptionsData={setExceptionsData} />
            }
          </PageInner>
        </Page>
      }
    </>
  );
};

const ExceptionsHome: FC = () => (
  <ExceptionsFiltersContextProvider>
    <WrappedExceptionsHome />
  </ExceptionsFiltersContextProvider>
);

export default ExceptionsHome;
